/**
 * Tabla de usuarios del resumen de Actividad (presentacional).
 *
 * Réplica de la tabla "Usuarios — clic para ver detalle" del SSR
 * (`user_activity.html`): posición, usuario (nombre + username), rol, total,
 * módulos (chips conteo) y un botón "Detalle". Sin lógica de datos: recibe los
 * usuarios y notifica al padre cuándo abrir el detalle de uno.
 */
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui";

import { moduleLabel, roleLabel, sortedCounts } from "../format";
import type { ActivityUser } from "../types";

interface ActivityUsersTableProps {
  users: ActivityUser[];
  onShowDetail: (user: ActivityUser) => void;
}

export function ActivityUsersTable({
  users,
  onShowDetail,
}: ActivityUsersTableProps) {
  if (users.length === 0) {
    return (
      <p className="px-1 py-8 text-center text-sm text-slate-500">
        Sin actividad en este período.
      </p>
    );
  }

  return (
    <Table>
      <TableHead>
        <TableRow>
          <TableHeaderCell className="w-10">#</TableHeaderCell>
          <TableHeaderCell>Usuario</TableHeaderCell>
          <TableHeaderCell>Rol</TableHeaderCell>
          <TableHeaderCell className="text-right">Total</TableHeaderCell>
          <TableHeaderCell>Módulos</TableHeaderCell>
          <TableHeaderCell className="text-right">Acciones</TableHeaderCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {users.map((user, index) => {
          const modules = sortedCounts(user.modulos);
          return (
            <TableRow key={user.username}>
              <TableCell className="font-semibold text-slate-500">
                {index + 1}
              </TableCell>
              <TableCell>
                <span className="font-medium text-slate-900">{user.name}</span>
                <br />
                <span className="text-xs text-slate-500">{user.username}</span>
              </TableCell>
              <TableCell>
                <span className="inline-flex items-center rounded bg-slate-100 px-1.5 py-0.5 text-xs font-medium text-slate-600">
                  {roleLabel(user.role)}
                </span>
              </TableCell>
              <TableCell className="text-right text-base font-bold text-slate-900">
                {user.total}
              </TableCell>
              <TableCell>
                <div className="flex flex-wrap gap-1">
                  {modules.map((entry) => (
                    <span
                      key={entry.key}
                      className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600"
                    >
                      {moduleLabel(entry.key)} · {entry.count}
                    </span>
                  ))}
                </div>
              </TableCell>
              <TableCell>
                <div className="flex justify-end">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => onShowDetail(user)}
                  >
                    Detalle
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
